import React, { useEffect, useMemo, useState } from 'react';
import { Button, Form, Modal, Spinner } from 'react-bootstrap';

import { buildJianyingSlotSections } from '@/features/automation/jianying/utils/jianyingSlots.js';
import {
  createJianyingTemplateGroup,
  deleteJianyingTemplateGroup,
  updateJianyingTemplateGroup,
  updateJianyingTemplateSlot,
} from '../../../services/jianyingTemplateService.js';

const KIND_META = {
  text: { icon: 'bi-fonts', label: '文字', tone: 'text' },
  image: { icon: 'bi-image', label: '图片', tone: 'image' },
  video: { icon: 'bi-camera-video', label: '视频', tone: 'video' },
  audio: { icon: 'bi-music-note-beamed', label: '音频', tone: 'audio' },
};

const errorMessage = (error, fallback) => (
  error.response?.data?.message || error.message || fallback
);

const toSeconds = (microseconds) => Number(microseconds || 0) / 1_000_000;

const getNodeRange = (node) => {
  if (node.type === 'slot') {
    const start = toSeconds(
      node.slot.slot_settings?.timeline_start_us
      ?? node.slot.slot_settings?.target_start_us
    );
    const duration = toSeconds(
      node.slot.slot_settings?.timeline_duration_us
      ?? node.slot.slot_settings?.target_duration_us
    );
    return { start, end: start + duration };
  }
  const settings = node.group.group_settings;
  const timelineStartUs = Number(settings?.timeline_start_us);
  const timelineDurationUs = Number(settings?.timeline_duration_us);
  if (
    Number.isFinite(timelineStartUs)
    && Number.isFinite(timelineDurationUs)
  ) {
    const start = toSeconds(timelineStartUs);
    return {
      start,
      end: start + toSeconds(timelineDurationUs),
    };
  }
  const ranges = node.children.map(getNodeRange);
  return {
    start: ranges.length ? Math.min(...ranges.map((range) => range.start)) : 0,
    end: ranges.length ? Math.max(...ranges.map((range) => range.end)) : 0,
  };
};

const sortTimelineNodes = (nodes) => (
  nodes
    .map((node) => (
      node.type === 'group'
        ? { ...node, children: sortTimelineNodes(node.children) }
        : node
    ))
    .sort((left, right) => (
      getNodeRange(left).start - getNodeRange(right).start
      || getNodeRange(left).end - getNodeRange(right).end
      || String(left.key).localeCompare(String(right.key))
    ))
);

const collectNodes = (nodes) => nodes.flatMap((node) => (
  node.type === 'group' ? [node, ...collectNodes(node.children)] : [node]
));

const hasVisibleNode = (node, visibleKeys) => (
  visibleKeys.has(node.key)
  || (node.type === 'group' && collectNodes(node.children).some((child) => visibleKeys.has(child.key)))
);

const GroupTreeNode = ({ activeGroupId, depth, node, onSelect }) => (
  <>
    <button
      type="button"
      className={`jianying-config-tree__row ${activeGroupId === node.key ? 'is-selected' : ''}`}
      style={{ '--jy-tree-depth': depth }}
      onClick={() => onSelect(node)}
    >
      <i className="bi bi-diamond-fill" />
      <span>{node.group.name}</span>
      <small>{collectNodes(node.children).filter((item) => item.type === 'slot').length}</small>
    </button>
    {node.children.filter((child) => child.type === 'group').map((child) => (
      <GroupTreeNode activeGroupId={activeGroupId} key={child.key} depth={depth + 1} node={child} onSelect={onSelect} />
    ))}
  </>
);

const SlotListCard = ({
  batchMode = false,
  node,
  onSelect,
  onToggleBatch,
  selectedForBatch = false,
  selectedKey,
}) => {
  const meta = KIND_META[node.slot.kind] || KIND_META.text;
  const isReplaceable = node.slot.replace_enabled !== false;
  return (
    <button
      type="button"
      className={`jianying-config-list__slot is-${meta.tone} ${selectedKey === node.key ? 'is-selected' : ''} ${batchMode ? 'is-batch' : ''} ${selectedForBatch ? 'is-batch-selected' : ''}`}
      onClick={() => (batchMode ? onToggleBatch(node.key) : onSelect(node))}
      role={batchMode ? 'checkbox' : undefined}
      aria-checked={batchMode ? selectedForBatch : undefined}
    >
      {batchMode ? (
        <span className="jianying-config-list__batch-check" aria-hidden="true">
          {selectedForBatch ? <i className="bi bi-check-lg" /> : null}
        </span>
      ) : null}
      <i className={`bi ${meta.icon}`} />
      <span>
        <strong>{node.slot.name}</strong>
        {node.slot.original_content ? <small>{node.slot.original_content}</small> : null}
      </span>
      <span className="jianying-config-list__tags">
        <em>{meta.label}</em>
        {!isReplaceable ? <em className="jianying-config-replace-tag">不可替换</em> : null}
      </span>
    </button>
  );
};

const GroupList = ({
  batchMode,
  node,
  onSelect,
  onToggleBatch,
  selectedKey,
  selectedSlotIds,
  visibleKeys,
}) => (
  <details className="jianying-config-list__group" open>
    <summary
      className={selectedKey === node.key ? 'is-selected' : ''}
      onClick={() => !batchMode && onSelect(node)}
    >
      <span><strong>{node.group.name}</strong><small>{node.children.length} 个直接对象</small></span>
      <em>{collectNodes(node.children).filter((item) => item.type === 'slot').length} 个槽位</em>
    </summary>
    <div>
      {node.children.filter((child) => hasVisibleNode(child, visibleKeys)).map((child) => (
        child.type === 'group'
          ? (
            <GroupList
              key={child.key}
              batchMode={batchMode}
              node={child}
              onSelect={onSelect}
              onToggleBatch={onToggleBatch}
              selectedKey={selectedKey}
              selectedSlotIds={selectedSlotIds}
              visibleKeys={visibleKeys}
            />
          )
          : (
            <SlotListCard
              key={child.key}
              batchMode={batchMode}
              node={child}
              onSelect={onSelect}
              onToggleBatch={onToggleBatch}
              selectedForBatch={selectedSlotIds.has(child.key)}
              selectedKey={selectedKey}
            />
          )
      ))}
    </div>
  </details>
);

const TimelineNode = ({
  depth,
  duration,
  groupPath = [],
  node,
  onSelect,
  selectedKey,
  visibleKeys,
}) => {
  const [expanded, setExpanded] = useState(true);
  const range = getNodeRange(node);
  const left = duration > 0 ? (range.start / duration) * 100 : 0;
  const width = duration > 0
    ? Math.max(((range.end - range.start) / duration) * 100, 1.2)
    : 2;
  const isGroup = node.type === 'group';
  const meta = isGroup ? null : KIND_META[node.slot.kind] || KIND_META.text;
  const label = isGroup ? node.group.name : node.slot.name;
  const isReplaceable = isGroup || node.slot.replace_enabled !== false;

  return (
    <>
      <div
        className={`jianying-config-timeline__row ${selectedKey === node.key ? 'is-selected' : ''}`}
        style={{ '--jy-tree-depth': depth }}
      >
        <button
          type="button"
          className="jianying-config-timeline__label"
          onClick={() => onSelect(node)}
        >
          {isGroup ? (
            <span
              className="jianying-config-timeline__fold"
              role="button"
              tabIndex={0}
              aria-label={expanded ? '折叠组' : '展开组'}
              aria-expanded={expanded}
              onClick={(event) => {
                event.stopPropagation();
                setExpanded((value) => !value);
              }}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  event.stopPropagation();
                  setExpanded((value) => !value);
                }
              }}
            >
              <i className={`bi bi-chevron-${expanded ? 'down' : 'right'}`} />
            </span>
          ) : <span className="jianying-config-timeline__fold" />}
          <i className={`bi ${isGroup ? 'bi-collection-play' : meta.icon}`} aria-hidden="true" />
          <span>
            <strong>{label}</strong>
            <small>
              {isGroup ? `${node.children.length} 项` : meta.label}
              {!isGroup ? ` · ${groupPath.length ? groupPath.join(' / ') : '未分组'}` : null}
              {!isGroup ? <time className="jianying-config-timeline__start-time">{range.start.toFixed(1)}s</time> : null}
              {!isGroup && !isReplaceable ? <span className="jianying-config-replace-tag">不可替换</span> : null}
            </small>
          </span>
        </button>
        <div className="jianying-config-timeline__track" aria-label={`${label} 时间位置`}>
          <span
            className={`jianying-config-timeline__clip ${isGroup ? 'is-group' : `is-${meta.tone}`}`}
            style={{ left: `${Math.min(left, 99)}%`, width: `${Math.min(width, 100 - Math.min(left, 99))}%` }}
          >
            {range.end > range.start ? `${range.start.toFixed(1)}s` : ''}
          </span>
        </div>
      </div>
      {isGroup && expanded ? node.children.filter((child) => hasVisibleNode(child, visibleKeys)).map((child) => (
        <TimelineNode
          key={child.key}
          depth={depth + 1}
          duration={duration}
          groupPath={isGroup ? [...groupPath, label] : groupPath}
          node={child}
          onSelect={onSelect}
          selectedKey={selectedKey}
          visibleKeys={visibleKeys}
        />
      )) : null}
    </>
  );
};

const GroupForm = ({ busy, groups, node, onDelete, onSave }) => {
  const [draft, setDraft] = useState(node.group);
  useEffect(() => setDraft(node.group), [node]);
  return (
    <div className="jianying-config-inspector__form">
      <div className="jianying-config-inspector__eyebrow">业务展示组</div>
      <Form.Group>
        <Form.Label>组名称</Form.Label>
        <Form.Control
          value={draft.name || ''}
          onChange={(event) => setDraft((current) => ({ ...current, name: event.target.value }))}
        />
      </Form.Group>
      <Form.Group>
        <Form.Label>父级组</Form.Label>
        <Form.Select
          value={draft.parent_group_id || ''}
          onChange={(event) => setDraft((current) => ({
            ...current,
            parent_group_id: event.target.value || null,
          }))}
        >
          <option value="">模板根层级</option>
          {groups.filter((group) => group.id !== node.key).map((group) => (
            <option key={group.id} value={group.id}>{group.name}</option>
          ))}
        </Form.Select>
      </Form.Group>
      <Form.Group>
        <Form.Label>排序</Form.Label>
        <Form.Control
          type="number"
          min="0"
          value={draft.sort_order ?? 0}
          onChange={(event) => setDraft((current) => ({
            ...current,
            sort_order: Number(event.target.value),
          }))}
        />
      </Form.Group>
      <div className="jianying-config-inspector__actions">
        <Button variant="outline-danger" disabled={busy} onClick={() => onDelete(node)}>
          删除组
        </Button>
        <Button disabled={busy || !draft.name?.trim()} onClick={() => onSave(draft)}>
          {busy ? <Spinner size="sm" /> : '保存组'}
        </Button>
      </div>
    </div>
  );
};

const SlotForm = ({ busy, groups, node, onSave }) => {
  const [draft, setDraft] = useState(node.slot);
  useEffect(() => setDraft(node.slot), [node]);
  const meta = KIND_META[draft.kind] || KIND_META.text;
  const settings = draft.slot_settings || {};
  const setSetting = (key, value) => setDraft((current) => ({
    ...current,
    slot_settings: { ...(current.slot_settings || {}), [key]: value },
  }));
  return (
    <div className="jianying-config-inspector__form">
      <div className="jianying-config-inspector__eyebrow">
        <i className={`bi ${meta.icon}`} /> {meta.label}槽位
      </div>
      <Form.Group>
        <Form.Label>槽位名称</Form.Label>
        <Form.Control
          value={draft.name || ''}
          onChange={(event) => setDraft((current) => ({ ...current, name: event.target.value }))}
        />
      </Form.Group>
      <Form.Group>
        <Form.Label>所属展示组</Form.Label>
        <Form.Select
          value={draft.group_id || ''}
          onChange={(event) => setDraft((current) => ({
            ...current,
            group_id: event.target.value || null,
          }))}
        >
          <option value="">未分组</option>
          {groups.map((group) => <option key={group.id} value={group.id}>{group.name}</option>)}
        </Form.Select>
      </Form.Group>
      <Form.Group>
        <Form.Label>模板默认内容</Form.Label>
        <Form.Control as="textarea" rows={3} value={draft.original_content || '—'} readOnly />
        <Form.Text>扫描模板时读取，仅用于确认当前槽位。</Form.Text>
      </Form.Group>
      <div className="jianying-config-inspector__switches">
        <Form.Check
          type="switch"
          id={`slot-enabled-${draft.id}`}
          label="可替换"
          checked={draft.replace_enabled !== false}
          onChange={(event) => setDraft((current) => ({
            ...current,
            replace_enabled: event.target.checked,
          }))}
        />
        <Form.Check
          type="switch"
          id={`slot-required-${draft.id}`}
          label="必填"
          checked={draft.required === true}
          disabled={draft.replace_enabled === false}
          onChange={(event) => setDraft((current) => ({
            ...current,
            required: event.target.checked,
          }))}
        />
        {draft.replace_enabled === false ? (
          <Form.Text>不可替换槽位不参与必填校验，原设置会保留。</Form.Text>
        ) : null}
      </div>
      {draft.kind === 'text' ? (
        <div className="jianying-config-inspector__rules">
          <Form.Group>
            <Form.Label>输入方式</Form.Label>
            <Form.Select value={settings.input_mode || 'single_line'} onChange={(event) => setSetting('input_mode', event.target.value)}>
              <option value="single_line">单行</option>
              <option value="multiline">多行</option>
            </Form.Select>
          </Form.Group>
          <Form.Group>
            <Form.Label>最大字数</Form.Label>
            <Form.Control type="number" min="1" value={settings.max_length || ''} onChange={(event) => setSetting('max_length', event.target.value ? Number(event.target.value) : null)} />
          </Form.Group>
          {settings.input_mode === 'multiline' ? (
            <Form.Group>
              <Form.Label>最大行数</Form.Label>
              <Form.Control type="number" min="1" value={settings.max_lines || ''} onChange={(event) => setSetting('max_lines', event.target.value ? Number(event.target.value) : null)} />
            </Form.Group>
          ) : null}
        </div>
      ) : null}
      {draft.kind === 'image' ? (
        <Form.Check
          type="switch"
          id={`slot-fixed-size-${draft.id}`}
          label="保持模板尺寸"
          checked={settings.fixed_size === true}
          onChange={(event) => setSetting('fixed_size', event.target.checked)}
        />
      ) : null}
      <Button disabled={busy || !draft.name?.trim()} onClick={() => onSave(draft)}>
        {busy ? <Spinner size="sm" /> : '保存槽位'}
      </Button>
    </div>
  );
};

const BATCH_UNCHANGED = '__unchanged__';

const BatchForm = ({ busy, groups, onApply, onCancel, selectedCount }) => {
  const [draft, setDraft] = useState({
    group_id: BATCH_UNCHANGED,
    replace_enabled: BATCH_UNCHANGED,
    required: BATCH_UNCHANGED,
  });
  const patch = {};
  if (draft.group_id !== BATCH_UNCHANGED) patch.group_id = draft.group_id || null;
  if (draft.replace_enabled !== BATCH_UNCHANGED) {
    patch.replace_enabled = draft.replace_enabled === 'true';
  }
  if (
    draft.replace_enabled !== 'false'
    && draft.required !== BATCH_UNCHANGED
  ) {
    patch.required = draft.required === 'true';
  }
  const hasChanges = Object.keys(patch).length > 0;

  return (
    <div className="jianying-config-inspector__form jianying-config-batch-form">
      <div className="jianying-config-inspector__eyebrow">批量配置</div>
      <div className="jianying-config-batch-form__summary">
        <strong>已选择 {selectedCount} 个槽位</strong>
        <span>只有不是“不修改”的字段会应用到所选槽位。</span>
      </div>
      <Form.Group>
        <Form.Label>所属展示组</Form.Label>
        <Form.Select value={draft.group_id} onChange={(event) => setDraft((current) => ({ ...current, group_id: event.target.value }))}>
          <option value={BATCH_UNCHANGED}>不修改</option>
          <option value="">未分组</option>
          {groups.map((group) => <option key={group.id} value={group.id}>{group.name}</option>)}
        </Form.Select>
      </Form.Group>
      <Form.Group>
        <Form.Label>可替换</Form.Label>
        <Form.Select value={draft.replace_enabled} onChange={(event) => setDraft((current) => ({ ...current, replace_enabled: event.target.value }))}>
          <option value={BATCH_UNCHANGED}>不修改</option>
          <option value="true">启用</option>
          <option value="false">关闭</option>
        </Form.Select>
      </Form.Group>
      <Form.Group>
        <Form.Label>必填规则</Form.Label>
        <Form.Select
          value={draft.required}
          disabled={draft.replace_enabled === 'false'}
          onChange={(event) => setDraft((current) => ({ ...current, required: event.target.value }))}
        >
          <option value={BATCH_UNCHANGED}>不修改</option>
          <option value="true">设为必填</option>
          <option value="false">设为选填</option>
        </Form.Select>
        {draft.replace_enabled === 'false' ? (
          <Form.Text>关闭替换时不修改已有必填设置。</Form.Text>
        ) : null}
      </Form.Group>
      <Button disabled={busy || !selectedCount || !hasChanges} onClick={() => onApply(patch)}>
        {busy ? <Spinner size="sm" /> : '应用到已选槽位'}
      </Button>
      <Button variant="outline-secondary" disabled={busy} onClick={onCancel}>退出批量配置</Button>
    </div>
  );
};

const JianyingTemplateTimelineEditor = ({ onChanged, template }) => {
  const [selectedKey, setSelectedKey] = useState('');
  const [slotScopeKey, setSlotScopeKey] = useState('all');
  const [batchMode, setBatchMode] = useState(false);
  const [selectedSlotIds, setSelectedSlotIds] = useState(() => new Set());
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  const [viewMode, setViewMode] = useState('timeline');
  const [search, setSearch] = useState('');
  const [replaceabilityFilter, setReplaceabilityFilter] = useState('all');
  const [pendingDeleteGroup, setPendingDeleteGroup] = useState(null);
  const [newGroup, setNewGroup] = useState({ name: '', parent_group_id: '' });
  const sections = useMemo(
    () => buildJianyingSlotSections(template, null, {
      includeDisabled: true,
      includeEmptyGroups: true,
    }),
    [template],
  );
  const allNodes = useMemo(() => collectNodes(sections), [sections]);
  const selectedNode = allNodes.find((node) => node.key === selectedKey) || null;
  const scopeGroupNode = allNodes.find(
    (node) => node.type === 'group' && node.key === slotScopeKey,
  ) || null;
  const scopedNodeKeys = useMemo(() => {
    if (slotScopeKey === 'all') return null;
    if (slotScopeKey === 'ungrouped') {
      return new Set(
        sections.filter((node) => node.type === 'slot').map((node) => node.key),
      );
    }
    if (!scopeGroupNode) return new Set();
    return new Set([
      scopeGroupNode.key,
      ...collectNodes(scopeGroupNode.children).map((node) => node.key),
    ]);
  }, [scopeGroupNode, sections, slotScopeKey]);
  const groups = Array.isArray(template.slot_groups) ? template.slot_groups : [];
  const duration = Math.max(
    1,
    ...allNodes.map((node) => getNodeRange(node).end),
  );
  const timelineTrackWidth = Math.max(360, Math.ceil(duration * 12));
  const tickCount = Math.max(1, Math.ceil(timelineTrackWidth / 120));

  useEffect(() => {
    if (selectedKey && !allNodes.some((node) => node.key === selectedKey)) {
      setSelectedKey('');
    }
  }, [allNodes, selectedKey]);

  useEffect(() => {
    if (slotScopeKey !== 'all' && slotScopeKey !== 'ungrouped' && !scopeGroupNode) {
      setSlotScopeKey('all');
    }
  }, [scopeGroupNode, slotScopeKey]);

  useEffect(() => {
    const existingSlotIds = new Set(
      allNodes.filter((node) => node.type === 'slot').map((node) => node.key),
    );
    setSelectedSlotIds((current) => {
      const next = new Set([...current].filter((slotId) => existingSlotIds.has(slotId)));
      return next.size === current.size ? current : next;
    });
  }, [allNodes]);

  const run = async (action, fallback) => {
    setBusy(true);
    setMessage('');
    try {
      await action();
      await onChanged();
    } catch (error) {
      setMessage(errorMessage(error, fallback));
    } finally {
      setBusy(false);
    }
  };

  const createGroup = () => {
    const name = newGroup.name.trim();
    if (!name) return;
    void run(async () => {
      const group = await createJianyingTemplateGroup(template.id, {
        name,
        parent_group_id: newGroup.parent_group_id || null,
        sort_order: groups.length,
      });
      setNewGroup({ name: '', parent_group_id: '' });
      setSelectedKey(group.id);
    }, '新建展示组失败');
  };

  const selectNode = (node) => {
    if (batchMode) return;
    if (node.type === 'group') setSlotScopeKey(node.key);
    setSelectedKey(node.key);
  };

  const selectRoot = () => {
    setSlotScopeKey('all');
    setSelectedKey('');
  };

  const selectUngrouped = () => {
    setSlotScopeKey('ungrouped');
    setSelectedKey('');
  };

  const toggleBatchMode = () => {
    setBatchMode((current) => {
      const next = !current;
      if (next) {
        setViewMode('list');
        setSelectedKey('');
      } else {
        setSelectedSlotIds(new Set());
      }
      return next;
    });
  };

  const toggleBatchSlot = (slotId) => {
    setSelectedSlotIds((current) => {
      const next = new Set(current);
      if (next.has(slotId)) next.delete(slotId); else next.add(slotId);
      return next;
    });
  };

  const applyBatchPatch = (patch) => {
    if (!selectedSlotIds.size || !Object.keys(patch).length) return;
    void run(async () => {
      await Promise.all([...selectedSlotIds].map((slotId) => (
        updateJianyingTemplateSlot(template.id, slotId, patch)
      )));
      setSelectedSlotIds(new Set());
    }, '批量配置槽位失败');
  };

  const confirmDeleteGroup = () => {
    if (!pendingDeleteGroup) return;
    const groupId = pendingDeleteGroup.group.id;
    setPendingDeleteGroup(null);
    setSelectedKey('');
    setSlotScopeKey('all');
    void run(
      () => deleteJianyingTemplateGroup(template.id, groupId),
      '删除展示组失败',
    );
  };

  const ungroupedNodes = sections.filter((node) => node.type === 'slot');
  const groupNodes = sections.filter((node) => node.type === 'group');
  const visibleGroupNodes = scopeGroupNode ? [scopeGroupNode] : groupNodes;
  const scopedTimelineNodes = useMemo(
    () => sortTimelineNodes(
      scopeGroupNode ? [scopeGroupNode] : slotScopeKey === 'ungrouped'
        ? sections.filter((node) => node.type === 'slot')
        : sections,
    ),
    [scopeGroupNode, sections, slotScopeKey],
  );
  const normalizedSearch = search.trim().toLocaleLowerCase();
  const visibleNodes = allNodes.filter((node) => {
    const matchesScope = !scopedNodeKeys || scopedNodeKeys.has(node.key);
    const value = node.type === 'group' ? node.group.name : node.slot.name;
    const matchesSearch = !normalizedSearch
      || String(value || '').toLocaleLowerCase().includes(normalizedSearch);
    const matchesReplaceability = replaceabilityFilter === 'all'
      || (node.type === 'slot' && (
        replaceabilityFilter === 'replaceable'
          ? node.slot.replace_enabled !== false
          : node.slot.replace_enabled === false
      ));
    return matchesScope && matchesSearch && matchesReplaceability;
  });
  const visibleKeys = new Set(visibleNodes.map((node) => node.key));
  const visibleTimelineNodes = scopedTimelineNodes.filter(
    (node) => hasVisibleNode(node, visibleKeys),
  );

  return (
    <div className="jianying-config-layout">
      <aside className="jianying-config-tree">
        <header className="jianying-config-pane-head">
          <span><strong>业务展示结构</strong><small>管理员可重新组合槽位</small></span>
        </header>
        <div className="jianying-config-tree__body">
          <button type="button" className={`jianying-config-tree__row is-root ${slotScopeKey === 'all' ? 'is-selected' : ''}`} onClick={selectRoot}>
            <i className="bi bi-grid-3x3-gap-fill" /><span>全部槽位</span><small>{template.slots?.length || 0}</small>
          </button>
          {groupNodes.map((node) => (
            <GroupTreeNode activeGroupId={scopeGroupNode?.key || ''} key={node.key} depth={0} node={node} onSelect={selectNode} />
          ))}
          {ungroupedNodes.length ? (
            <button type="button" className={`jianying-config-tree__row is-ungrouped ${slotScopeKey === 'ungrouped' ? 'is-selected' : ''}`} onClick={selectUngrouped}>
              <i className="bi bi-circle" /><span>未分组素材</span><small>{ungroupedNodes.length}</small>
            </button>
          ) : null}
          <p className="jianying-config-tree__note">扫描生成的组只是默认业务展示结构，可继续改名、嵌套或重新组合。</p>
        </div>
        <div className="jianying-config-tree__create">
          <Form.Select aria-label="新组父级" value={newGroup.parent_group_id} onChange={(event) => setNewGroup((current) => ({ ...current, parent_group_id: event.target.value }))}>
            <option value="">顶层组</option>
            {groups.map((group) => <option key={group.id} value={group.id}>{group.name}</option>)}
          </Form.Select>
          <Form.Control aria-label="新组名称" placeholder="新展示组名称" value={newGroup.name} onChange={(event) => setNewGroup((current) => ({ ...current, name: event.target.value }))} onKeyDown={(event) => event.key === 'Enter' && createGroup()} />
          <Button size="sm" disabled={busy || !newGroup.name.trim()} onClick={createGroup}>＋ 新建组</Button>
        </div>
      </aside>

      <section className="jianying-config-timeline">
        <header className="jianying-config-timeline__toolbar">
          <div>
            <strong>{viewMode === 'timeline' ? '槽位时间轴' : '全部槽位'}</strong>
            <small>{template.slots?.length || 0} 个槽位 · 时间位置只读</small>
          </div>
          <div className="jianying-config-timeline__tools">
            <span className="jianying-config-readonly"><i className="bi bi-lock" /> 时间只读</span>
            <Form.Select
              className="jianying-config-replace-filter"
              aria-label="筛选替换状态"
              value={replaceabilityFilter}
              onChange={(event) => setReplaceabilityFilter(event.target.value)}
            >
              <option value="all">全部</option>
              <option value="replaceable">可替换</option>
              <option value="fixed">不可替换</option>
            </Form.Select>
            <button type="button" className={`jianying-config-batch-toggle ${batchMode ? 'is-active' : ''}`} onClick={toggleBatchMode}>
              {batchMode ? '退出批量配置' : '批量配置'}
            </button>
            <div className="jianying-config-view-switch" aria-label="槽位展示方式">
              <button type="button" className={viewMode === 'timeline' ? 'is-active' : ''} disabled={batchMode} onClick={() => setViewMode('timeline')}>时间轴</button>
              <button type="button" className={viewMode === 'list' ? 'is-active' : ''} onClick={() => setViewMode('list')}>列表</button>
            </div>
            <Form.Control aria-label="搜索槽位" placeholder="搜索槽位" value={search} onChange={(event) => setSearch(event.target.value)} />
          </div>
        </header>
        {message ? <p className="jianying-notice" role="alert">{message}</p> : null}
        {viewMode === 'timeline' ? (
          <>
            <div className="jianying-config-timeline__caption">组和槽位按时间位置展示；展开组即可查看组内槽位。</div>
            <div className="jianying-config-timeline__viewport">
              <div
                className="jianying-config-timeline__canvas"
                style={{ '--jy-timeline-track-width': `${timelineTrackWidth}px` }}
              >
                <div className="jianying-config-timeline__ruler">
                  <span />
                  <div>
                    {Array.from({ length: tickCount + 1 }, (_, index) => (
                      <span key={index} style={{ left: `${(index / tickCount) * 100}%` }}>
                        {((duration * index) / tickCount).toFixed(1)}s
                      </span>
                    ))}
                  </div>
                </div>
                <div className="jianying-config-timeline__rows">
                  {visibleTimelineNodes.map((node) => (
                    <TimelineNode key={node.key} depth={0} duration={duration} node={node} onSelect={selectNode} selectedKey={selectedKey} visibleKeys={visibleKeys} />
                  ))}
                  {!visibleTimelineNodes.length ? <div className="jianying-empty">没有匹配的槽位</div> : null}
                </div>
              </div>
            </div>
          </>
        ) : (
          <div className="jianying-config-list">
            {visibleGroupNodes.filter((node) => slotScopeKey !== 'ungrouped' && hasVisibleNode(node, visibleKeys)).map((node) => (
              <GroupList key={node.key} batchMode={batchMode} node={node} onSelect={selectNode} onToggleBatch={toggleBatchSlot} selectedKey={selectedKey} selectedSlotIds={selectedSlotIds} visibleKeys={visibleKeys} />
            ))}
            {ungroupedNodes.filter((node) => visibleKeys.has(node.key)).length ? (
              <section className="jianying-config-list__ungrouped">
                <header><strong>未分组素材</strong><small>直接属于模板根层级</small></header>
                {ungroupedNodes.filter((node) => visibleKeys.has(node.key)).map((node) => (
                  <SlotListCard key={node.key} batchMode={batchMode} node={node} onSelect={selectNode} onToggleBatch={toggleBatchSlot} selectedForBatch={selectedSlotIds.has(node.key)} selectedKey={selectedKey} />
                ))}
              </section>
            ) : null}
            {!visibleNodes.length ? <div className="jianying-empty">没有匹配的槽位</div> : null}
          </div>
        )}
      </section>

      <aside className="jianying-config-inspector">
        {batchMode ? (
          <BatchForm
            busy={busy}
            groups={groups}
            onApply={applyBatchPatch}
            onCancel={toggleBatchMode}
            selectedCount={selectedSlotIds.size}
          />
        ) : null}
        {!batchMode && selectedNode?.type === 'group' ? (
          <GroupForm
            busy={busy}
            groups={groups}
            node={selectedNode}
            onDelete={setPendingDeleteGroup}
            onSave={(group) => run(
              () => updateJianyingTemplateGroup(template.id, group.id, {
                name: group.name.trim(),
                parent_group_id: group.parent_group_id || null,
                sort_order: Number(group.sort_order) || 0,
              }),
              '保存展示组失败',
            )}
          />
        ) : null}
        {!batchMode && selectedNode?.type === 'slot' ? (
          <SlotForm
            busy={busy}
            groups={groups}
            node={selectedNode}
            onSave={(slot) => run(
              () => updateJianyingTemplateSlot(template.id, slot.id, {
                name: slot.name.trim(),
                group_id: slot.group_id || null,
                replace_enabled: slot.replace_enabled !== false,
                required: slot.required === true,
                sort_order: Number(slot.sort_order) || 0,
                slot_settings: slot.slot_settings || {},
              }),
              '保存槽位失败',
            )}
          />
        ) : null}
        {!batchMode && !selectedNode ? (
          <div className="jianying-config-inspector__empty">
            <i className="bi bi-cursor" />
            <strong>{slotScopeKey === 'ungrouped' ? '未分组素材' : '选择时间轴中的组或槽位'}</strong>
            <span>{slotScopeKey === 'ungrouped' ? '未分组不是业务组；请选择具体槽位后再配置。' : '组可任意嵌套；槽位定位信息保持只读。'}</span>
          </div>
        ) : null}
      </aside>
      <Modal show={Boolean(pendingDeleteGroup)} centered onHide={() => setPendingDeleteGroup(null)}>
        <Modal.Header closeButton>
          <Modal.Title>删除组“{pendingDeleteGroup?.group.name}”？</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {pendingDeleteGroup ? (() => {
            const descendants = collectNodes(pendingDeleteGroup.children);
            const slotCount = descendants.filter((node) => node.type === 'slot').length;
            const childGroupCount = descendants.filter((node) => node.type === 'group').length;
            return slotCount || childGroupCount ? (
              <p>
                该组包含 {slotCount} 个后代槽位{childGroupCount ? `和 ${childGroupCount} 个子组` : ''}。
                删除后，当前组和全部子组都会删除，所有后代槽位一起移动到“未分组”；槽位配置、素材定位和文案内容都会保留。
              </p>
            ) : <p>该组为空。删除后无法恢复。</p>;
          })() : null}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" disabled={busy} onClick={() => setPendingDeleteGroup(null)}>取消</Button>
          <Button variant="danger" disabled={busy} onClick={confirmDeleteGroup}>删除组并移到未分组</Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default JianyingTemplateTimelineEditor;
